import type { TableV2ColumnMeta } from "../types";
import { AGGREGATION_LABELS } from "./aggregations";
import { SHOW_AS_LABELS } from "./showAs";
import type { PivotField, PivotValue } from "./types";

const PERCENT_SHOW_AS = new Set([
  "pctOfRow",
  "pctOfColumn",
  "pctOfTotal",
  "pctChangeFromPrevious",
  "vsSamePeriodLastYear",
]);

export const fieldLabel = <TData>(fields: PivotField<TData>[], id: string) =>
  fields.find((field) => field.id === id)?.label ?? id;

export const valueLabel = <TData>(
  fields: PivotField<TData>[],
  value: PivotValue,
) => {
  if (value.label) return value.label;
  const base = `${AGGREGATION_LABELS[value.aggregation]} of ${fieldLabel(fields, value.field)}`;
  const showAs = value.showAs ?? "value";
  return showAs === "value" ? base : `${base} (${SHOW_AS_LABELS[showAs]})`;
};

export const valueMeta = <TData>(
  fields: PivotField<TData>[],
  value: PivotValue,
): TableV2ColumnMeta => {
  const showAs = value.showAs ?? "value";
  if (PERCENT_SHOW_AS.has(showAs)) return { type: "percent" };
  if (showAs === "rank") return { type: "number", decimals: 0 };
  if (value.aggregation === "count" || value.aggregation === "distinctCount") {
    return { type: "number", decimals: 0 };
  }
  const field = fields.find((f) => f.id === value.field);
  // Formatting only; the source column's total, menu items and cell styles don't apply to pivot values
  const type = field?.meta?.type ?? field?.type;
  return {
    type: type === "currency" || type === "percent" ? type : "number",
    decimals: field?.meta?.decimals,
    abbreviate: field?.meta?.abbreviate,
    negative: field?.meta?.negative,
  };
};

export const defaultAggregation = <TData>(
  field: PivotField<TData> | undefined,
) =>
  field?.type === "number" ||
  field?.type === "currency" ||
  field?.type === "percent"
    ? "sum"
    : "count";
